import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { EditorView } from "@/components/ui/editor"
import { PdfDocumentViewer } from "@/modules/patients/detail/conclude/document-viewer"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { CARE_PLAN_SUMMARY } from "@/modules/patients/types/ProgressNoteDocument"

export interface CarePlanViewerProps {
  id: string
  progressNoteId: string
  fileId: string | null | undefined
  signedAt: string | null | undefined
}

export const CarePlanViewer = ({ id, progressNoteId, fileId, signedAt }: CarePlanViewerProps) => {
  const [showPreview, setShowPreview] = useState(true)
  const { t } = useTranslation()

  if (!fileId || !signedAt) {
    return <CarePlanSummaryDocumentPreview id={id} type={CARE_PLAN_SUMMARY} progressNoteId={progressNoteId} />
  }

  return (
    <div className="relative h-full">
      <Button className="absolute top-3 right-10 px-6" onClick={() => setShowPreview((preview) => !preview)}>
        {showPreview ? t("patient.conclude.treatmentPlan.viewPdf") : t("patient.conclude.treatmentPlan.viewPreview")}
      </Button>
      {showPreview && (
        <CarePlanSummaryDocumentPreview id={id} type={CARE_PLAN_SUMMARY} progressNoteId={progressNoteId} />
      )}
      {!showPreview && <PdfDocumentViewer signedAt={signedAt} fileId={fileId} />}
    </div>
  )
}

export const CarePlanSummaryDocumentPreview = (document: ProgressNoteDocument) => {
  const progressNoteAndParentData = useEpisodeOfCareStoreContext((state) => state.progressNoteAndParentData)
  const progressNote = progressNoteAndParentData.find(
    (p) => p.episodeOfCareProgressNoteData.id === document.progressNoteId,
  )?.episodeOfCareProgressNoteData
  const markdownPreviews = useConcludeDialogStoreContext((state) => state.markdownPreviews)
  if (!progressNote) {
    // This issue only occurs when conclude dialog was previously opened,
    // and the progress note got removed meanwhile.
    // Then it still exists in conclude store but not anymore in Relay,
    // and it is just temporarily used, until another render selects the proper first document.
    return
  }
  const markdownPreview = markdownPreviews.find(
    (m) => m.saveCarePlanData.carePlanPreviewOutput?.progressNoteId === progressNote.id,
  )
  if (!markdownPreview?.saveCarePlanData.carePlanPreviewOutput?.previewMarkdown) {
    return
  }
  return (
    <div className="h-full overflow-auto rounded-xl border p-3">
      <div data-testid="care-plan-summary-document" className="grid gap-4">
        <EditorView
          name="carePlanSummaryDocumentPreview"
          initialValue={markdownPreview.saveCarePlanData.carePlanPreviewOutput.previewMarkdown}
        />
      </div>
    </div>
  )
}
